<template>
  <div class="container">
    <div v-if="removedRecupHolidays != null || removedRecupHolidays.length !== 0">
      <div v-if="removedRecupHolidays.length === 1" class="alert alert-warning" role="alert">
        Onvoldoende recup feestdag beschikbaar. De ingeplande recup feestdag op {{removedRecupHolidays[0]}} werd verwijderd.
      </div>
      <div v-if="removedRecupHolidays.length > 1" class="alert alert-warning" role="alert">
        Onvoldoende recup feestdag beschikbaar. De ingeplande recup feestdagen op volgende dagen werden verwijderd:
        <li v-for="removedRecupHoliday in removedRecupHolidays" v-bind:key="removedRecupHoliday">{{removedRecupHoliday}}</li>
      </div>
    </div>
    <div class="row justify-content-center">
      <button
          class="btn-basic btn-fill-pulse-dark" @click="removeAll"
          v-if="!isCompleted"
          :disabled="validateRemoveAll || plannedShifts.length === 0">Verwijder shifts
      </button>
    </div>
    <div
        class="alert alert-warning" role="alert"
        v-if="completedMappingObject[selectedContract.id]">
      Timesheet is bevestigd. Je kan hier geen aanpassingen meer doen.
    </div>
    <FullCalendar
        :events="getEvents()"
        default-view="dayGridMonth"
        locale="nl"
        :firstDay="1"
        :default-date='initialMonth'
        :plugins="calendarPlugins"
        :header=" {left: 'title',center: '',right: getButtons()} "
        :weekends="true"
        :selectable="true"
        :selectLongPressDelay="0"
        :longPressDelay="200"
        @select="handleSelect"
        ref="fullCalendar"
        :selectOverlap="getOverlap"
        :showNonCurrentDates="false"
        @eventClick="handleEventClick"
        :height="500"
        :contentHeight="500"
        :customButtons="customButtons"/>

    <div class="row border p-3">
      <div class="col-lg-12">
        <p>
          Feestdagen hebben een <span class="text-red">rode</span> achtergrond
          <br>
          Dagen waarop al een shift staat hebben een <span class="bg-lightgreen">groene</span>
          achtergrond
        </p>
        <p>Ingevuld:
          <span :class="getClassForWorkedHours()">
               {{ countedHours | convertDotToCommaInNumber }}
           </span>
          /{{ neededHours | convertDotToCommaInNumber }} uren
        </p>
        <p :class="getClassForWorkedHours()">
          Uren recup verworven deze maand: {{ countedHours - neededHours | numberToHumanReadableHours }}
        </p>
        <p>Opgebouwde Recuperatie: {{ recupPreviousHours | convertDotToCommaInNumber }} uren / {{ recupPreviousInDays | convertDotToCommaInNumber  }} dagen
          <br>
          Opgenomen Recuperatie: {{ recupTakenHours | convertDotToCommaInNumber }} uren / {{ recupTakenInDays | convertDotToCommaInNumber  }} dagen
          <br>
          Resterende vakantie: {{ remainingVacationDays | convertDotToCommaInNumber }} dagen
          <br>
          Resterende recuperatie feestdagen: {{remainingRecuperationHolidays | convertDotToCommaInNumber}}
        </p>
      </div>
      <div class="justify-content-center col-lg-12" v-if="!isLocked">
        <button
            class="btn-basic btn-fill-pulse" style="width: 180px;"
            @click="handleConfirm">Planning vastzetten
        </button>
      </div>
    </div>

    <CreateShiftModal :currentCalendarEntry="currentCalendarEntry" :contract="selectedContract"/>
    <EditOrDeleteEventModal :currentCalendarEntry="currentCalendarEntry" :contract="selectedContract"/>
    <DeleteAbsenceModal :currentCalendarEntry="currentCalendarEntry"/>
    <TooltipModal :currentUser="currentUser"/>
  </div>
</template>
<script>

import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import {mapActions, mapGetters, mapMutations} from "vuex";
import {VoerroModal} from '@voerro/vue-modal';
import moment from "moment";
import PlannedShift from "../../domain/PlannedShift";
import {toYearAndMonth} from "@/util/DateUtil";
import {isHalfDayEvent, sickLeaveList, titleToAbsenceObject, vacationList} from "@/constants/Absences";
import TooltipModal from "@/components/modals/TooltipModal";
import CreateShiftModal from "@/components/modals/CreateShiftModal";
import EditOrDeleteEventModal from "@/components/modals/EditOrDeleteShiftModal";
import DeleteAbsenceModal from "@/components/modals/DeleteAbsenceModal";
import {numberToHumanReadableHours} from "@/util/FormattingUtil";

export default  {
  name: "ContractDashboard",
  components: {
    DeleteAbsenceModal,
    EditOrDeleteEventModal,
    CreateShiftModal,
    TooltipModal,
    FullCalendar,
  },
  props: ['contract'],
  data() {
    return {
      calendarPlugins: [dayGridPlugin, interactionPlugin, timeGridPlugin, listPlugin],
      currentCalendarEntry: {},
      fullCalendar: null,
      firstDayOfSelectedMonth: "",
      lastDayOfSelectedMonth: "",
      vacationList,
      initialMonth: toYearAndMonth(this.currentlySelectedMonth),
      sickLeaveList,
      customButtons: {
        next: {
          click: () => this.clickNext()
        },
        prev: {
          click: () => this.clickPrev()
        }
      }
    };
  },
  computed: {
    ...mapGetters({
      selectedContract: "timesheet/selectedContract",
      plannedShifts: "timesheet/plannedShifts",
      holidays: "timesheet/holidays",
      occupiedDays: "timesheet/occupiedDays",
      currentUser: "user/currentLoggedInUser",
      absenceDays: "timesheet/absenceDays",
      countedHours: "timesheet/countedHours",
      neededHours: "timesheet/neededHours",
      workedEnough: "timesheet/workedEnough",
      remainingRecuperationHolidays: "timesheet/remainingRecuperationHolidays",
      recupPreviousHours: 'timesheet/recupPreviousHours',
      recupTakenHours: 'timesheet/recupTakenHours',
      recupPreviousInDays: 'timesheet/recupPreviousInDays',
      recupTakenInDays: 'timesheet/recupTakenInDays',
      remainingVacationDays: 'timesheet/remainingVacationDays',
      canAddRecup: 'timesheet/canAddRecup',
      completedMappingObject: "timesheet/completedMappingObject",
      isLocked: "timesheet/isLocked",
      isCompleted: "timesheet/completed",
      previousMonthAvailable: "timesheet/previousMonthAvailable",
      nextMonthAvailable: "timesheet/nextMonthAvailable",
      removedRecupHolidays: "timesheet/removedRecupHolidays",
      currentlySelectedMonth: "time/currentlySelectedMonth"
    }),
    validateRemoveAll() {
      return this.plannedShifts.filter(plannedShift => plannedShift.adjusted).length > 0;
    }
  },
  created() {
    this.resetStateBeforeRenderingFullCalendar();
    this.fetchAbsenceData();
    this.fetchPlannedData();
  },
  mounted() {
    this.firstDayOfSelectedMonth = this.getFirstDayOfMonthAsDateString();
    this.lastDayOfSelectedMonth = this.getLastDayOfMonthAsDateString();
    this.fullCalendar = this.$refs.fullCalendar.getApi();
    this.fullCalendar.gotoDate(toYearAndMonth(this.currentlySelectedMonth));

    if (this.$device && (this.$device.ios || this.$device.android) && this.currentUser && this.currentUser.firstLogin) {
      VoerroModal.show('tooltip');
    }
  },
  methods: {
    numberToHumanReadableHours,
    ...mapActions("timesheet", [
      'removeAllShifts',
      'fetchContracts',
      'fetchPlannedData',
      'fetchAbsenceData',
      'lockPlanning'
    ]),
    ...mapMutations({
      resetStateBeforeRenderingFullCalendar: "timesheet/resetStateBeforeRenderingFullCalendar",
      addMonth: "time/addMonth",
      subtractMonth: "time/subtractMonth"
    }),
    handleSelect(arg) {
      const datumInArg = arg.start.toLocaleDateString("en-CA");
      const checkDate = date => date.start.toLocaleString() === datumInArg;
      const dateIsOccupied = this.occupiedDays.some(checkDate);

      if (!dateIsOccupied) {
        this.currentCalendarEntry = new PlannedShift();
        this.currentCalendarEntry.start = arg.start;
        this.currentCalendarEntry.end = arg.end;
        VoerroModal.show('createShift');
      }
    },
    handleEventClick(arg) {
      const isAnAbsence = arg.event.extendedProps.absenceCategory;
      this.currentCalendarEntry = arg.event;
      if (isAnAbsence) {
        VoerroModal.show('deleteAbsence');
      } else if (this.eventIsNotEdited(arg.event.title)) {
        VoerroModal.show('editOrDeleteShift');
      }
    },
    eventIsNotEdited(eventTitle) {
      return !eventTitle.startsWith("*");
    },
    isHalfDayAbsence(event) {
      if (event.extendedProps.absenceCategory) {
        return isHalfDayEvent(titleToAbsenceObject[event.title]);
      }
      return false;
    },
    getOverlap(event) {
        return event.rendering === 'background' || this.isHalfDayAbsence(event);
    },
    getFirstDayOfMonthAsDateString() {
      return toYearAndMonth(this.currentlySelectedMonth) + "-01";
    },
    getLastDayOfMonthAsDateString() {
      const yearMonthSplit = toYearAndMonth(this.currentlySelectedMonth).split("-");

      const year = parseInt(yearMonthSplit[0]);
      const month = parseInt(yearMonthSplit[1]);
      const lastDayOfMonth = new Date(year, month, 0);
      return moment(lastDayOfMonth).format("YYYY-MM-DD");
    },
    removeAll() {
      this.removeAllShifts(toYearAndMonth(this.currentlySelectedMonth));
    },
    getClassForWorkedHours() {
      return this.workedEnough ? 'text-green' : 'text-red';
    },
    clickNext() {
        this.disableNextButton();
        this.addMonth();
        this.fullCalendar.next();
        this.fetchDataForShownMonth();
        this.checkAndReloadHeaderButton('next');
    },
    clickPrev() {
        this.disablePrevButton();
        this.subtractMonth();
        this.fullCalendar.prev();
        this.fetchDataForShownMonth();
        this.checkAndReloadHeaderButton('prev');
    },
    checkAndReloadHeaderButton(button) {
      if (button === 'prev') {
        if (this.previousMonthAvailable) {
          this.reloadHeaderButton(".fc-prev-button");
        }
      }
      else if (button === 'next') {
        if (this.nextMonthAvailable) {
          this.reloadHeaderButton(".fc-next-button");
        }
      }
    },
    reloadHeaderButton(buttonSelector) {
      let button = document.querySelector(buttonSelector);
      button.disabled = true;
      setTimeout(function () {
        button.disabled = false;
      }, 1000);
    },
    async fetchDataForShownMonth() {
      this.firstDayOfSelectedMonth = this.getFirstDayOfMonthAsDateString();
      this.lastDayOfSelectedMonth = this.getLastDayOfMonthAsDateString();

      await this.fetchContracts();
      this.fetchAbsenceData();
      this.fetchPlannedData();
    },
    getEvents() {
      return [
        ...this.plannedShifts,
        ...this.holidays,
        ...this.occupiedDays,
        ...this.absenceDays,
        ...this.getWeekendDays()
      ];
    },
    getWeekendDays() {
      return [{
        daysOfWeek: [0, 6],
        rendering: 'background',
        color: '#D0D0D0',
        allDay: true
      }];
    },
    handleConfirm() {
      if (confirm('Ben je zeker dat je de planning wilt vastzetten? Hierna kan je deze niet opnieuw vastzetten.')) {
        this.lockPlanning();
      }
    },
    getButtons() {
      const prev = this.previousMonthAvailable ? 'prev ' : '';
      const next = this.nextMonthAvailable ? 'next' : '';
      return prev + next;
    },
    disablePrevButton(){
      let prevButton = document.querySelector(".fc-prev-button");
      prevButton.disabled = true;
    },
    disableNextButton(){
      let nextButton = document.querySelector(".fc-next-button");
      nextButton.disabled = true;
    },
  }
};
</script>

<style scoped>
@import '~@fullcalendar/core/main.css';
@import '~@fullcalendar/daygrid/main.css';

.text-red {
  color: red;
}

/*noinspection CssUnusedSymbol*/
.text-green {
  color: green;
}

.bg-lightgreen {
  background-color: lightgreen;
}
.element-style {
   cursor: not-allowed;
 }
.planned-event {
  cursor: pointer;
}
</style>
